import { useEffect, useState, useCallback } from "react";
import { ScrollText, RefreshCw, ShieldAlert } from "lucide-react";
import { api, useAuth, formatApiError } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ACTION_LABELS = {
  connexion: "Connexion",
  connexion_echouee: "Connexion échouée",
  deconnexion: "Déconnexion",
  export_excel: "Export Excel",
  rappel_manuel: "Rappel manuel envoyé",
  rappel_test: "Test des rappels email",
  utilisateur_cree: "Utilisateur créé",
  utilisateur_modifie: "Utilisateur modifié",
  utilisateur_supprime: "Utilisateur supprimé",
  role_cree: "Rôle créé",
  role_modifie: "Rôle modifié",
  role_supprime: "Rôle supprimé",
  annonce_publiee: "Annonce publiée",
  annonce_modifiee: "Annonce modifiée",
  annonce_supprimee: "Annonce supprimée",
  certificat_supprime: "Certificat supprimé",
  personnalisation_modifiee: "Personnalisation modifiée",
  personnalisation_image: "Image du site modifiée",
  import_incendie_pdf: "Import PDF incendie",
  annonce_publiee: "Annonce publiée",
  code_connexion_envoye: "Code de connexion envoyé",
  code_connexion_renvoye: "Code de connexion renvoyé",
};

const ACTION_COLORS = {
  connexion: "border-emerald-700 bg-emerald-950 text-emerald-400",
  connexion_echouee: "border-red-700 bg-red-950 text-red-400",
  deconnexion: "border-gray-600 bg-gray-800 text-gray-300",
};

function badgeClass(action) {
  if (ACTION_COLORS[action]) return ACTION_COLORS[action];
  if (action.includes("supprime") || action.includes("supprimee")) return "border-red-700 bg-red-950 text-red-400";
  return "border-blue-800 bg-blue-950/60 text-blue-400";
}

export default function Journal() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/logs").then((r) => setLogs(r.data.logs))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="journal-denied">
        <ShieldAlert className="h-10 w-10 text-amber-400 mb-3" />
        <p className="font-semibold">Accès réservé à l'administrateur</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Sécurité</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <ScrollText className="h-8 w-8 text-accent" /> Journal d'activité
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Connexions, exports, suppressions et gestion des accès — visible par vous seul (500 derniers événements).</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="journal-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Actualiser
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="journal-table">
          <thead>
            <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Date / heure</th>
              <th className="px-4 py-3">Événement</th>
              <th className="px-4 py-3">Utilisateur</th>
              <th className="px-4 py-3">Détail</th>
              <th className="px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Chargement...</td></tr>}
            {!loading && logs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground" data-testid="journal-empty">Aucun événement enregistré pour le moment.</td></tr>}
            {!loading && logs.map((l) => (
              <tr key={l.id} className="border-b border-border/50" data-testid={`journal-row-${l.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{new Date(l.ts).toLocaleString("fr-FR")}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${badgeClass(l.action)}`}>
                    {ACTION_LABELS[l.action] || l.action}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs">{l.email}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[220px] truncate">{l.detail || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="journal-count">{logs.length} événement(s) affiché(s)</p>
    </div>
  );
}
